import { useCallback } from 'react';
import type { ClipboardHandlers } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { useAppDispatch } from '../../store';

/**
 * Provides standardized clipboard copy handlers for ReadOnlyItem and EntityId components.
 *
 * Automatically integrates with the Redux toast notification system to show success/error messages
 * that include the copied text (e.g., "Copied to clipboard: 12345").
 *
 * @returns {ClipboardHandlers} Object with onCopySuccess and onCopyError callbacks
 *
 * @example
 * // Basic usage with ReadOnlyItem
 * const MyComponent = () => {
 *   const clipboardHandlers = useClipboardHandlers();
 *
 *   return (
 *     <ReadOnlyItem
 *       label="Template ID"
 *       value={123}
 *       copyOnClick
 *       {...clipboardHandlers}
 *     />
 *   );
 * };
 *
 * @example
 * // Usage with multiple components
 * const DetailsView = ({ data }) => {
 *   const clipboardHandlers = useClipboardHandlers();
 *
 *   return (
 *     <>
 *       <EntityId id={data.id} code={data.code} {...clipboardHandlers} />
 *       <ReadOnlyItem label="Name" value={data.name} copyOnClick {...clipboardHandlers} />
 *     </>
 *   );
 * };
 *
 * @example
 * // With custom display but specific copy text
 * const StatusDisplay = ({ status }) => {
 *   const clipboardHandlers = useClipboardHandlers();
 *
 *   return (
 *     <ReadOnlyItem
 *       label="Status"
 *       value={<StatusBadge status={status} />}
 *       copyText={status} // Copy the text value, not the component
 *       copyOnClick
 *       {...clipboardHandlers}
 *     />
 *   );
 * };
 */
export const useClipboardHandlers = (): ClipboardHandlers => {
  const dispatch = useAppDispatch();

  const onCopySuccess = useCallback(
    (text: string) => {
      dispatch(addToast({ message: `Copied to clipboard: ${text}` }));
    },
    [dispatch],
  );

  const onCopyError = useCallback(
    (text: string, error: Error) => {
      dispatch(addToast({ message: `Failed to copy to clipboard: ${text}`, isError: true }));
      // eslint-disable-next-line no-console -- intentional for error logging
      console.error('Failed to copy to clipboard:', text, error);
    },
    [dispatch],
  );

  return {
    onCopySuccess,
    onCopyError,
  };
};
